import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, CreditCard, Settings, LogOut, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const accountMenuItems = [
  { path: '/account/profile', icon: User, label: 'Profile' },
  { path: '/account/billing', icon: CreditCard, label: 'Billing / Subscription' },
  { path: '/account/settings', icon: Settings, label: 'Account Settings' },
];

// Founder-only menu items
const founderMenuItems = [
  { path: '/account/team-members', icon: Users, label: 'Team Members' },
];

export function UserMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { user, logout, isFounder } = useAuth();

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
    navigate('/login');
  };

  // Get initials from user name
  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="user-menu-container" ref={menuRef}>
      <button
        className="user-avatar-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Account menu"
        data-testid="user-avatar-btn"
      >
        <div className="user-avatar-circle">
          <span>{getInitials(user?.name)}</span>
        </div>
      </button>

      {isOpen && (
        <div className="user-dropdown" data-testid="user-dropdown">
          <div className="user-dropdown-header">
            <div className="user-dropdown-name">{user?.name || 'User'}</div>
            <div className="user-dropdown-email">{user?.email || ''}</div>
          </div>
          <div className="user-dropdown-divider"></div>
          <div className="user-dropdown-section">
            {accountMenuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="user-dropdown-link"
                onClick={() => setIsOpen(false)}
                data-testid={`account-link-${item.path.split('/').pop()}`}
              >
                <item.icon size={16} className="user-dropdown-icon" />
                <span>{item.label}</span>
              </Link>
            ))}
            {/* Founder-only menu items */}
            {isFounder() && founderMenuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="user-dropdown-link"
                onClick={() => setIsOpen(false)}
                data-testid={`account-link-${item.path.split('/').pop()}`}
              >
                <item.icon size={16} className="user-dropdown-icon" />
                <span>{item.label}</span>
              </Link>
            ))}
          </div>
          <div className="user-dropdown-divider"></div>
          <button
            className="user-dropdown-link user-dropdown-logout"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut size={16} className="user-dropdown-icon" />
            <span>Logout</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default UserMenu;
